import { useEffect, useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useUserRole } from "@/hooks/useUserRole";
import {
  AffiliateConfigCard,
  type AffiliateConfig,
} from "@/components/admin/affiliate/AffiliateConfigCard";
import {
  AffiliateLedgerCard,
  type OverrideAction,
} from "@/components/admin/affiliate/AffiliateLedgerCard";
import { AffiliateOverrideDialog } from "@/components/admin/affiliate/AffiliateOverrideDialog";
import {
  ACTION_LABEL,
  ACTION_NEEDS_NOTES,
} from "@/components/admin/affiliate/overrideActions";
import { AffiliateKpiPanels } from "@/components/admin/affiliate/AffiliateKpiPanels";
import type { AffiliateCommissionRow } from "@/lib/affiliateDashboard";

export function AffiliateTab() {
  const { isSuperAdmin } = useUserRole();

  const [config, setConfig] = useState<AffiliateConfig | null>(null);
  const [savingConfig, setSavingConfig] = useState(false);
  const [configLoading, setConfigLoading] = useState(true);

  const [statusFilter, setStatusFilter] = useState<
    AffiliateCommissionRow["status"] | "all"
  >("all");
  const [classFilter, setClassFilter] = useState<"standard" | "super" | "all">(
    "standard",
  );
  const [rows, setRows] = useState<AffiliateCommissionRow[]>([]);
  const [rowsLoading, setRowsLoading] = useState(true);

  const [overrideRow, setOverrideRow] = useState<AffiliateCommissionRow | null>(null);
  const [overrideAction, setOverrideAction] = useState<OverrideAction>("approve");
  const [overrideNotes, setOverrideNotes] = useState("");
  const [overrideRunning, setOverrideRunning] = useState(false);

  const loadConfig = async () => {
    setConfigLoading(true);
    const { data, error } = await supabase
      .from("affiliate_program_config")
      .select("commission_bps, holdback_days, lapse_grace_days, min_payout_cents, paid_only_required")
      .maybeSingle();
    setConfigLoading(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    setConfig(data ?? null);
  };

  const loadRows = async () => {
    setRowsLoading(true);
    let q = supabase
      .from("affiliate_commissions")
      .select(
        "id, affiliate_user_id, affiliate_profile_id, is_super_affiliate, referred_user_id, stripe_event_id, stripe_invoice_id, stripe_subscription_id, gross_amount_cents, commission_bps, commission_amount_cents, currency, status, accrued_at, approved_at, reversed_at, forfeited_at, paid_at, reverses_commission_id, notes",
      )
      .order("accrued_at", { ascending: false })
      .limit(100);
    if (statusFilter !== "all") q = q.eq("status", statusFilter);
    if (classFilter === "standard") q = q.eq("is_super_affiliate", false);
    if (classFilter === "super") q = q.eq("is_super_affiliate", true);
    const { data, error } = await q;
    setRowsLoading(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    setRows((data ?? []) as AffiliateCommissionRow[]);
  };

  useEffect(() => {
    void loadConfig();
  }, []);

  useEffect(() => {
    void loadRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, classFilter]);

  const saveConfig = async () => {
    if (!config) return;
    setSavingConfig(true);
    const { error } = await supabase
      .from("affiliate_program_config")
      .update({
        commission_bps: config.commission_bps,
        holdback_days: config.holdback_days,
        lapse_grace_days: config.lapse_grace_days,
        min_payout_cents: config.min_payout_cents,
        paid_only_required: config.paid_only_required,
      })
      .eq("id", true);
    setSavingConfig(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Affiliate config saved");
  };

  const openOverride = (row: AffiliateCommissionRow, action: OverrideAction) => {
    setOverrideRow(row);
    setOverrideAction(action);
    setOverrideNotes("");
  };

  const runOverride = async () => {
    if (!overrideRow) return;
    if (ACTION_NEEDS_NOTES[overrideAction] && overrideNotes.trim().length === 0) {
      toast.error("Notes are required for this action");
      return;
    }
    setOverrideRunning(true);
    const { error } = await supabase.rpc("admin_override_commission", {
      _commission_id: overrideRow.id,
      _action: overrideAction,
      _notes: overrideNotes.trim() || null,
    });
    setOverrideRunning(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(`${ACTION_LABEL[overrideAction]} applied`);
    setOverrideRow(null);
    await loadRows();
  };

  return (
    <div className="space-y-6">
      <AffiliateConfigCard
        config={config}
        setConfig={setConfig}
        loading={configLoading}
        saving={savingConfig}
        isSuperAdmin={isSuperAdmin}
        onSave={saveConfig}
      />

      <AffiliateKpiPanels rows={rows} />

      <AffiliateLedgerCard
        rows={rows}
        rowsLoading={rowsLoading}
        isSuperAdmin={isSuperAdmin}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        classFilter={classFilter}
        setClassFilter={setClassFilter}
        onOverride={openOverride}
      />

      <AffiliateOverrideDialog
        row={overrideRow}
        action={overrideAction}
        notes={overrideNotes}
        setNotes={setOverrideNotes}
        running={overrideRunning}
        onClose={() => setOverrideRow(null)}
        onRun={runOverride}
      />
    </div>
  );
}
